import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useValidationRulesStore = defineStore("validationRules", {
  state: () => ({
    rules: [],
    fields: [],
    loading: false,
    saving: false,
    deleting: false,
    error: null
  }),

  getters: {
    hasRules: (state) => state.rules.length > 0,
    getRuleById: (state) => (id) => {
      return state.rules.find((rule) => rule.id === id)
    }
  },

  actions: {
    async fetchFields(documentTypeId) {
      this.error = null

      try {
        const response = await apiV1.get(`/document-types/${documentTypeId}/fields`)
        this.fields = response.data.data || []
        return this.fields
      } catch (error) {
        this.error = error.message
        this.fields = []
        throw error
      }
    },

    async fetchRulesByDocumentType(documentTypeId) {
      this.error = null

      try {
        const response = await apiV1.get(`/validation-rules`, {
          params: { document_type_id: documentTypeId }
        })
        this.rules = response.data.data || []
        return this.rules
      } catch (error) {
        this.error = error.message
        this.rules = []
        throw error
      }
    },

    async loadAll(documentTypeId) {
      this.loading = true
      this.error = null

      try {
        await this.fetchFields(documentTypeId)
        await this.fetchRulesByDocumentType(documentTypeId)
      } finally {
        this.loading = false
      }
    },

    async saveRule(documentTypeId, rule) {
      this.error = null

      const payload = {
        document_type_id: documentTypeId,
        name: rule.name,
        left_operands: rule.leftOperands.map((op) => ({ type: op.type, value: op.value })),
        left_operators: rule.leftOperators,
        comparison_operator: rule.comparisonOperator,
        right_operands: rule.rightOperands.map((op) => ({ type: op.type, value: op.value })),
        right_operators: rule.rightOperators,
        is_active: true
      }

      if (rule.id) {
        payload.id = rule.id
      }

      const response = await apiV1.post("/validation-rules", payload)
      const saved = response.data?.data || response.data

      if (rule.id) {
        const index = this.rules.findIndex((r) => r?.id === rule.id)
        if (index !== -1) {
          this.rules[index] = saved
        }
      } else if (saved?.id) {
        this.rules.push(saved)
      }

      return saved
    },

    async deleteRule(id) {
      this.deleting = true
      this.error = null

      try {
        await apiV1.delete(`/validation-rules/${id}`)
        this.rules = this.rules.filter((rule) => rule.id !== id)
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.deleting = false
      }
    },

    async deleteAllRules() {
      this.deleting = true
      this.error = null

      try {
        const deletePromises = this.rules
          .filter((rule) => rule.id)
          .map((rule) => apiV1.delete(`/validation-rules/${rule.id}`))

        await Promise.all(deletePromises)
        this.rules = []
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.deleting = false
      }
    },

    async saveAllRules(documentTypeId, rules) {
      this.saving = true
      this.error = null
      let lastMessage = null

      try {
        for (const rule of rules) {
          const result = await this.saveRule(documentTypeId, rule)
          if (result?.message) lastMessage = result.message
        }
        return { rules: this.rules, message: lastMessage }
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.saving = false
      }
    },

    clearRules() {
      this.rules = []
      this.fields = []
      this.error = null
    }
  }
})
